import type { TransportData } from '@/domainLogic/index.ts';
import type { AccData } from '@/app/common/memsearch/typesAccData.ts';
import OvernightFlightsMessage from '@/app/components/search/components/OvernightFlightsMessage';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import { ControlType } from '../../../Form';
import FilterContainer from '../FilterContainer';
import getRadioContainer from './getRadioContainer';
import type { CommonProps } from './CommonProps';

const calculateDaysBetweenDates = ({ from, to }: { from: Date; to: Date }) => {
  const fromDate = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toDate = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());

  return Math.floor((toDate - fromDate) / (1000 * 60 * 60 * 24));
};

const calculateOriginalDate = ({ transportData }: { transportData: TransportData[] }) => {
  const outboundDeparture = transportData?.[0]?.outbound?.segments?.[0]?.departure?.datetime;
  const inboundDeparture = transportData?.[0]?.inbound?.segments?.[0]?.departure?.datetime;
  return inboundDeparture && outboundDeparture
    ? calculateDaysBetweenDates({
        from: new Date(outboundDeparture),
        to: new Date(inboundDeparture),
      })
    : 0;
};

const getOvernightFlights = ({
  isWaiting,
  searchParams,
  transportData,
  accData,
  commonProps,
  localization,
  t,
}: {
  isWaiting: boolean;
  searchParams: SearchParams;
  transportData?: TransportData[];
  accData?: AccData;
  commonProps: CommonProps;
  localization: Localization;
  t: TranslationFunction;
}) => {
  const filterLabel = t('vacation.oss.overnight_flight.filter.label');
  const filterSelect = t('vacation.oss.overnight_flight.filter.select');
  const filterDeselect = t('vacation.oss.overnight_flight.filter.deselect');
  const filterSelectPlural = t('vacation.oss.overnight_flight.filter.select_plural');
  const filterDeselectPlural = t('vacation.oss.overnight_flight.filter.deselect_plural');

  const accNights = accData?.accNights || 0;
  const originalNumberOfNights = transportData ? calculateOriginalDate({ transportData }) : 0;
  const overnightNumberOfNights = searchParams[URLParameters.OVERNIGHT_FLIGHTS]
    ? accNights
    : originalNumberOfNights - 1;
  const deselectedKey = originalNumberOfNights > 1 ? filterDeselectPlural : filterDeselect;
  const selectedKey = overnightNumberOfNights > 1 ? filterSelectPlural : filterSelect;
  const deselectedLabel = deselectedKey.replace('__amount__', `${originalNumberOfNights}`);
  const selectedLabel = selectedKey.replace('__amount__', `${overnightNumberOfNights}`);

  const radioControl = getRadioContainer({
    fieldName: URLParameters.OVERNIGHT_FLIGHTS,
    containerTitle: filterLabel,
    containerName: FilterContainer.OvernightFlightsContainer,
    options: [
      {
        value: 0,
        label: deselectedLabel,
        disabled: isWaiting,
      },
      {
        value: 1,
        label: selectedLabel,
        disabled: isWaiting,
      },
    ],
    value: searchParams[URLParameters.OVERNIGHT_FLIGHTS] ? Number(searchParams[URLParameters.OVERNIGHT_FLIGHTS]) : 0,
    ...commonProps,
  });

  radioControl?.controls?.unshift({
    control: ControlType.LABEL,
    name: 'overnightFlightsMessage',
    content: <OvernightFlightsMessage localization={localization} />,
    ...commonProps,
  });

  return radioControl;
};
export default getOvernightFlights;
